/**
 * Tabs Configuration Factory
 *
 * Factory functions to build tab configurations for CompanyDetails.
 * Extracts the tabs array logic from the component for better testability
 * and reduced component complexity.
 */

import { DETAILS_POUP_TABS } from '../constants/tabs';

// Re-export for convenience (note: keeping original typo for compatibility)
export { DETAILS_POUP_TABS };

// Alias with correct spelling for tests
export const DETAILS_POPUP_TABS = DETAILS_POUP_TABS;

const ACCESS_DENIED_TOOLTIP = (
  <>
    Need full platform access?
    <br />
    Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for assistance.
  </>
);

/** Tooltip for the Financials tab based on access / empty / loading state. */
const getFinancialsTooltipContent = ({
  canViewExtendedData,
  isLoadingDetails,
  detailsLoadedFromEffect,
  isFinancialsConsolidatedDataEmpty,
  isFinancialsIndividualDataEmpty,
  loadDetailsError
}) => {
  if (!canViewExtendedData) return ACCESS_DENIED_TOOLTIP;
  if (isLoadingDetails && detailsLoadedFromEffect) return <>Loading details</>;
  if (
    isFinancialsConsolidatedDataEmpty &&
    isFinancialsIndividualDataEmpty &&
    !isLoadingDetails &&
    !loadDetailsError
  ) {
    return <>No financials available</>;
  }
  return null;
};

/** Tooltip when comparable companies cannot be listed. */
const getSimilarCompaniesTooltipContent = ({ revenue, loadSimilarCompaniesError }) => {
  if (!revenue) {
    return (
      <>
        The selected company has no revenue data. Therefore, no comparable companies are available.
      </>
    );
  }
  if (loadSimilarCompaniesError) return <>No comparable companies available</>;
  return null;
};

/**
 * Banks tab, or null once loaded details carry no bank data at all (the backend
 * field is missing). An empty list keeps the tab, greyed out. While loading or
 * after a load error it behaves like the other details tabs.
 */
const buildBanksTab = ({ state, canViewDetails }) => {
  const banks = state.bankConnections;
  const isFieldMissing = banks === undefined && !state.isLoadingDetails && !state.loadDetailsError;
  // Users without access never load details; they still see the tab with the access hint
  if (canViewDetails && isFieldMissing) return null;

  const isLoaded = !state.isLoadingDetails && !state.loadDetailsError;
  const isEmpty = canViewDetails && isLoaded && !banks?.length;
  const banksCount = banks?.length ? ` (${banks.length})` : '';

  let tooltipContent = null;
  if (!canViewDetails) tooltipContent = ACCESS_DENIED_TOOLTIP;
  else if (isEmpty) tooltipContent = <>No banks available</>;

  return {
    id: 'profiler_search_results_banks_tab',
    slug: DETAILS_POPUP_TABS.BANKS,
    name: `Banks${banksCount}`,
    isActive: state.tab === DETAILS_POPUP_TABS.BANKS,
    onClick: () => {
      if (canViewDetails) {
        state.setTab(DETAILS_POPUP_TABS.BANKS, 'profiler_search_results_banks_tab');
      }
    },
    isDisabled: !canViewDetails || isEmpty,
    tooltipContent,
    loadingIndicator: state.isLoadingDetails
  };
};

/**
 * Builds the main company details tabs configuration
 *
 * @param {Object} options
 * @param {Object} options.state - Local observable state
 * @param {Object} [options.similarCompaniesState] - Similar companies hook state (extracted from main state)
 * @param {Object} options.profilerAccessStore - Access control store
 * @param {Object} options.project - Current project
 * @param {Object} options.company - Company data
 * @param {boolean} options.isFromWatchList - Whether viewing from watchlist
 * @param {boolean} [options.isShareholderGraphLoading] - Whether the shareholder structure (the
 *   graph view request) is still pending; defaults to the details loading state
 * @returns {Array} Array of tab configuration objects
 */
export const buildCompanyDetailsTabs = ({
  state,
  similarCompaniesState,
  profilerAccessStore,
  project,
  company,
  isFromWatchList,
  isShareholderGraphLoading
}) => {
  // Use similarCompaniesState if provided, otherwise fall back to state (backwards compatible)
  const scState = similarCompaniesState || state;
  const canViewExtendedData = profilerAccessStore.canViewProfilerDetailsExtendedData(project);
  // Lite-inclusive gate (TD-2499): contacts + shareholders are available to Lite/Demo-Lite too.
  // The detailed Financials tab stays on canViewExtendedData (Full-only).
  const canViewDetails = profilerAccessStore.canViewProfilerDetails(project);

  const similarCompaniesCount = scState.similarCompanies?.length
    ? ` (${scState.similarCompanies.length})`
    : '';
  const keyContactsCount = state.keyContacts?.length ? ` (${state.keyContacts.length})` : '';

  const tabs = [
    {
      id: 'profiler_search_results_details_tab',
      slug: DETAILS_POPUP_TABS.GENERAL,
      name: 'General information',
      isActive: state.tab === DETAILS_POPUP_TABS.GENERAL,
      onClick: () => {
        state.setTab(DETAILS_POPUP_TABS.GENERAL, 'profiler_search_results_details_tab');
      },
      loadingIndicator: state.isLoadingDetails || state.isLoadingSupplementaryData
    },
    {
      id: 'profiler_search_results_product_tab',
      slug: DETAILS_POPUP_TABS.PRODUCT_PORTFOLIO,
      name: 'Product portfolio',
      isActive: state.tab === DETAILS_POPUP_TABS.PRODUCT_PORTFOLIO,
      onClick: () => {
        state.setTab(DETAILS_POPUP_TABS.PRODUCT_PORTFOLIO, 'profiler_search_results_product_tab');
      },
      loadingIndicator: state.isLoadingDetails || state.isLoadingSupplementaryData
    },
    {
      id: 'profiler_search_results_financials_tab',
      slug: DETAILS_POPUP_TABS.FINANCIALS,
      name: 'Financials',
      isActive: state.tab === DETAILS_POPUP_TABS.FINANCIALS,
      onClick: () => {
        if (canViewExtendedData) {
          state.setTab(DETAILS_POPUP_TABS.FINANCIALS, 'profiler_search_results_financials_tab');
        }
      },
      isDisabled:
        state.isLoadingDetails ||
        (state.isFinancialsConsolidatedDataEmpty &&
          state.isFinancialsIndividualDataEmpty &&
          !state.loadDetailsError) ||
        !canViewExtendedData,
      tooltipContent: getFinancialsTooltipContent({
        canViewExtendedData,
        isLoadingDetails: state.isLoadingDetails,
        detailsLoadedFromEffect: state.detailsLoadedFromEffect,
        isFinancialsConsolidatedDataEmpty: state.isFinancialsConsolidatedDataEmpty,
        isFinancialsIndividualDataEmpty: state.isFinancialsIndividualDataEmpty,
        loadDetailsError: state.loadDetailsError
      }),
      loadingIndicator: state.isLoadingDetails
    },
    {
      id: 'profiler_search_results_shareholder_tab',
      slug: DETAILS_POPUP_TABS.SHAREHOLDER_STRUCTURE,
      name: 'Shareholder structure',
      isActive: state.tab === DETAILS_POPUP_TABS.SHAREHOLDER_STRUCTURE,
      onClick: () => {
        if (canViewDetails) {
          state.setTab(
            DETAILS_POPUP_TABS.SHAREHOLDER_STRUCTURE,
            'profiler_search_results_shareholder_tab'
          );
        }
      },
      isDisabled: !canViewDetails,
      tooltipContent: !canViewDetails ? ACCESS_DENIED_TOOLTIP : null,
      loadingIndicator: isShareholderGraphLoading ?? state.isLoadingDetails
    },
    buildBanksTab({ state, canViewDetails }),
    {
      id: 'profiler_search_results_similar_companies_tab',
      slug: DETAILS_POPUP_TABS.SIMILAR_COMPANIES,
      name: `Comparable companies${similarCompaniesCount}`,
      isActive: state.tab === DETAILS_POPUP_TABS.SIMILAR_COMPANIES,
      onClick: () => {
        state.setTab(
          DETAILS_POPUP_TABS.SIMILAR_COMPANIES,
          'profiler_search_results_similar_companies_tab'
        );
      },
      isDisabled:
        !company.revenue ||
        !!scState.loadSimilarCompaniesError ||
        (scState.similarCompaniesInitialLoad && !scState.isComparableCompaniesTriggerSuccessful),
      tooltipContent: getSimilarCompaniesTooltipContent({
        revenue: company.revenue,
        loadSimilarCompaniesError: scState.loadSimilarCompaniesError
      }),
      loadingIndicator: scState.isLoadingSimilarCompanies
    },
    {
      id: 'profiler_search_results_key_contacts_tab',
      slug: DETAILS_POPUP_TABS.KEY_CONTACTS,
      name: `Key contacts${keyContactsCount}`,
      isActive: state.tab === DETAILS_POPUP_TABS.KEY_CONTACTS,
      isDisabled: !canViewDetails,
      onClick: () => {
        state.setTab(DETAILS_POPUP_TABS.KEY_CONTACTS, 'profiler_search_results_key_contacts_tab');
      },
      tooltipContent: !canViewDetails ? ACCESS_DENIED_TOOLTIP : null,
      loadingIndicator: state.isLoadingDetails
    }
  ].filter(Boolean);

  if (isFromWatchList) {
    tabs.push({
      id: 'profiler_search_results_updates_tab',
      slug: DETAILS_POPUP_TABS.UPDATES,
      name: 'News & updates',
      isActive: state.tab === DETAILS_POPUP_TABS.UPDATES,
      isDisabled: true,
      onClick: () => {
        // Future implementation
      }
    });
  }

  return tabs;
};
